/**
 * chipMaps.js — per-jurisdiction subject chips for TeachPanel (LatAm expansion
 * 2026-07-27). Each chip: { label (native, shown), payload (canonical English —
 * what the pipeline's subject enum expects), patterns/exclude (match the
 * jurisdiction's REAL standard_sets.subject spellings so the search scopes to
 * the right sets). Anything a chip misses is still reachable via "More subjects…".
 *
 * Profiles keyed by jurisdiction code; default 'us'. Patterns verified against
 * the live GET /api/states/:CODE/subjects spellings 2026-07-27.
 */

// US — the original four (labelKey → i18n, kept from the shipped SUBJECT_CHIPS).
const US_CHIPS = [
  { labelKey: 'teach.subjects.math', payload: 'Mathematics', patterns: [/math/i], exclude: [] },
  { labelKey: 'teach.subjects.readingWriting', payload: 'Language Arts', patterns: [/language arts|(^|\W)ela(\W|$)|reading|literacy/i], exclude: [/development|proficiency|financial literacy|computer|digital literacy|informational literacy/i] },
  { labelKey: 'teach.subjects.science', payload: 'Science', patterns: [/science/i], exclude: [/computer|social|histor/i] },
  { labelKey: 'teach.subjects.socialStudies', payload: 'Social Studies', patterns: [/social studies|social science|histor|civic/i], exclude: [/prehistor/i] },
];

// Brazil — BNCC componentes (pt labels).
const BR_CHIPS = [
  { label: 'Matemática', payload: 'Mathematics', patterns: [/matem[áa]tica/i], exclude: [] },
  { label: 'Língua Portuguesa', payload: 'Language Arts', patterns: [/l[íi]ngua portuguesa|linguagens/i], exclude: [] },
  { label: 'Ciências', payload: 'Science', patterns: [/ci[êe]ncias|biologia|f[íi]sica|qu[íi]mica/i], exclude: [/humanas|sociais|educa[çc][ãa]o f[íi]sica/i] },
  { label: 'História e Geografia', payload: 'Social Studies', patterns: [/hist[óo]ria|geografia|ci[êe]ncias humanas/i], exclude: [] },
];

// Mexico — Plan 2022. The primaria campo "Saberes y Pensamiento Científico"
// blends math + science, so the Math and Science chips both scope to it (the
// query text narrows within); Social Studies covers the Ética + De lo Humano
// campos. Four unique payloads to match the canonical selection model.
const MX_CHIPS = [
  { label: 'Matemáticas', payload: 'Mathematics', patterns: [/saberes|pensamiento cient[íi]fico|matem/i], exclude: [] },
  { label: 'Lenguajes', payload: 'Language Arts', patterns: [/lenguajes|espa[ñn]ol|ingl[ée]s|lengua ind[íi]gena/i], exclude: [] },
  { label: 'Ciencias', payload: 'Science', patterns: [/saberes|pensamiento cient[íi]fico|\bciencias?\b|biolog|\bf[íi]sica\b|qu[íi]mic/i], exclude: [/educaci[óo]n f[íi]sica/i] },
  { label: 'Sociedades y Comunidad', payload: 'Social Studies', patterns: [/[ée]tica|naturaleza y sociedades|historia|geograf|c[íi]vica|humano|comunitario|educaci[óo]n f[íi]sica|socioemocional|\bartes\b/i], exclude: [] },
];

// Chile — asignaturas (es labels).
const CL_CHIPS = [
  { label: 'Matemática', payload: 'Mathematics', patterns: [/matem[áa]tica|geometr[íi]a|probabilidades|l[íi]mites, derivadas/i], exclude: [] },
  { label: 'Lenguaje y Comunicación', payload: 'Language Arts', patterns: [/lenguaje|lengua y literatura|lectura|escritura|taller de literatura/i], exclude: [] },
  { label: 'Ciencias Naturales', payload: 'Science', patterns: [/ciencias naturales|biolog[íi]a|\bf[íi]sica\b|qu[íi]mica|ciencias de la salud/i], exclude: [] },
  { label: 'Historia y Cs. Sociales', payload: 'Social Studies', patterns: [/historia|geograf[íi]a|ciencias sociales|educaci[óo]n ciudadana|comprensi[óo]n hist[óo]rica/i], exclude: [] },
];

// Puerto Rico — DEPR core (3 programs live; no Estudios Sociales set ingested,
// so no Social-Studies chip — an empty-match chip would un-scope the search).
const PR_CHIPS = [
  { label: 'Matemáticas', payload: 'Mathematics', patterns: [/matem[áa]tica/i], exclude: [] },
  { label: 'Español', payload: 'Language Arts', patterns: [/espa[ñn]ol|lengua/i], exclude: [] },
  { label: 'Ciencias', payload: 'Science', patterns: [/ciencias/i], exclude: [] },
];

// France — national programmes (fr labels). Serves the French-Caribbean départements.
const FR_CHIPS = [
  { label: 'Mathématiques', payload: 'Mathematics', patterns: [/math[ée]matiques/i], exclude: [] },
  { label: 'Français', payload: 'Language Arts', patterns: [/fran[çc]ais/i], exclude: [] },
  { label: 'Sciences', payload: 'Science', patterns: [/sciences|technologie/i], exclude: [] },
  { label: 'Histoire-Géographie', payload: 'Social Studies', patterns: [/histoire|g[ée]ographie|\bemc\b|enseignement moral/i], exclude: [/arts/i] },
];

// Haiti — École Fondamentale matières (fr labels). Créole + Français are both "langue" (Language Arts);
// the two "Sciences" subjects stay disjoint (Expérimentales=Science, Sociales=Social Studies).
const HT_CHIPS = [
  { label: 'Mathématiques', payload: 'Mathematics', patterns: [/math[ée]matiques/i], exclude: [] },
  { label: 'Français / Créole', payload: 'Language Arts', patterns: [/fran[çc]ais|cr[ée]ole/i], exclude: [] },
  { label: 'Sciences Expérimentales', payload: 'Science', patterns: [/sciences exp[ée]rimentales/i], exclude: [] },
  { label: 'Sciences Sociales', payload: 'Social Studies', patterns: [/sciences sociales/i], exclude: [] },
];

// Generic LatAm Spanish (2026-07-28) — Ecuador/Argentina/Uruguay/Paraguay/El Salvador/Guatemala
// share consistent disciplines; one profile covers them all (patterns match each country's real
// set subject spellings; "More subjects…" catches anything a chip misses).
const ES_LATAM_CHIPS = [
  { label: 'Matemática', payload: 'Mathematics', patterns: [/matem[áa]tica/i], exclude: [] },
  { label: 'Lengua', payload: 'Language Arts', patterns: [/lengua|comunicaci[óo]n|lenguaje|literatura|castellano|espa[ñn]ol|idioma/i], exclude: [/matem/i] },
  { label: 'Ciencias Naturales', payload: 'Science', patterns: [/naturales|biolog[íi]a|f[íi]sica|qu[íi]mica|medio natural|ambiente|ciencias de la tierra/i], exclude: [/sociales/i] },
  { label: 'Ciencias Sociales', payload: 'Social Studies', patterns: [/sociales|historia|geograf[íi]a|ciudadan[íi]a|c[íi]vica|formaci[óo]n ciudadana/i], exclude: [/naturales/i] },
];

// English-Caribbean (2026-07-29) — covers OECS primary (Mathematics/Language Arts/Science/Social
// Studies) AND CXC secondary discrete subjects (Physics/Chemistry/Biology; Geography/History).
const EN_CARIBBEAN_CHIPS = [
  { label: 'Mathematics', payload: 'Mathematics', patterns: [/mathematics|(^|\W)math(\W|$)/i], exclude: [] },
  { label: 'English', payload: 'Language Arts', patterns: [/english|language arts/i], exclude: [] },
  { label: 'Science', payload: 'Science', patterns: [/science|physics|chemistry|biology/i], exclude: [/social/i] },
  { label: 'Social Studies', payload: 'Social Studies', patterns: [/social studies|geography|history|caribbean studies/i], exclude: [] },
];

const CHIP_MAPS = { us: US_CHIPS, br: BR_CHIPS, mx: MX_CHIPS, cl: CL_CHIPS, pr: PR_CHIPS, fr: FR_CHIPS, ht: HT_CHIPS, eslt: ES_LATAM_CHIPS, encar: EN_CARIBBEAN_CHIPS };
const CODE_TO_PROFILE = {
  BRA: 'br', MEX: 'mx', CHL: 'cl', PR: 'pr', FRA: 'fr',
  ECU: 'eslt', ARG: 'eslt', URY: 'eslt', PRY: 'eslt', SLV: 'eslt', GTM: 'eslt',
  COL: 'eslt', PER: 'eslt', BOL: 'eslt', HND: 'eslt', NIC: 'eslt', CRI: 'eslt', PAN: 'eslt', DOM: 'eslt',
  // Blocked-four expansion (2026-07-31): Haiti (fr, own profile); Venezuela + Cuba reuse the es profile.
  HTI: 'ht', VEN: 'eslt', CUB: 'eslt',
  // English-Caribbean (OECS primary + CXC secondary): the 9 OECS territories + 6 CXC-only countries.
  // JAM also hosts the NSC primary sets now (2026-07-31) — encar's 4 core chips still cover them.
  LCA: 'encar', ATG: 'encar', DMA: 'encar', GRD: 'encar', KNA: 'encar', VCT: 'encar', MSR: 'encar', AIA: 'encar', VGB: 'encar',
  JAM: 'encar', TTO: 'encar', BRB: 'encar', GUY: 'encar', BLZ: 'encar', BHS: 'encar',
};

/** Chip set for a jurisdiction code (US default). */
export function chipsForJurisdiction(code) {
  return CHIP_MAPS[CODE_TO_PROFILE[String(code || '').toUpperCase()] ?? 'us'];
}
export { US_CHIPS };
